import { type ReactElement } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EnforcementSeverityThresholdControl } from '../EnforcementSeverityThresholdControl';
import {
  SecretConfidenceThresholdControl,
  SecretConfidenceInfoTooltip,
} from '../SecretConfidenceThresholdControl';
import {
  useOrgPolicySettings,
  type OrgPolicySettingsFlags,
} from './useOrgPolicySettings';

type Props = OrgPolicySettingsFlags & {
  organizationId: string;
};

function LoadError({ setting }: { setting: string }): ReactElement {
  return (
    <div className="rounded-md bg-destructive/15 p-4">
      <p className="text-sm text-destructive font-medium">
        Failed to load {setting}
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        Please retry or contact support if the issue persists.
      </p>
    </div>
  );
}

/**
 * Org Policies → Policy Settings (KIR-5106). Selects stay locked until the
 * stored value loads, so the `'high'` fallback is never saved over it.
 */
export function OrgPolicySettingsPage({
  organizationId,
  isEnforcePolicyModeEnabled,
  isSecretConfidenceThresholdEnabled,
}: Props): ReactElement {
  const {
    hasAnyEnforcePolicy,
    severity,
    isLoadingSeverity,
    hasSeverityError,
    isSavingSeverity,
    onSeverityChange,
    confidence,
    isLoadingConfidence,
    hasConfidenceError,
    isSavingConfidence,
    onConfidenceChange,
  } = useOrgPolicySettings(organizationId, {
    isEnforcePolicyModeEnabled,
    isSecretConfidenceThresholdEnabled,
  });

  return (
    <div className="space-y-4">
      {isEnforcePolicyModeEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Enforcement Severity Threshold</CardTitle>
            <CardDescription>
              When policies are set to Enforce, this threshold determines the
              minimum severity at which protective actions are applied.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasSeverityError ? (
              <LoadError setting="enforcement severity threshold" />
            ) : (
              <EnforcementSeverityThresholdControl
                value={severity}
                onChange={onSeverityChange}
                disabled={isLoadingSeverity || !hasAnyEnforcePolicy}
                saving={isSavingSeverity}
              />
            )}
          </CardContent>
        </Card>
      )}

      {isSecretConfidenceThresholdEnabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1.5">
              Secret Alert Confidence Threshold
              <SecretConfidenceInfoTooltip />
            </CardTitle>
            <CardDescription>
              Secret leakage alerts below the chosen confidence are hidden from
              the dashboard, won't notify, and won't be blocked in Enforce mode.
              Secrets matched by the regex engine have no confidence score and
              are always shown.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasConfidenceError ? (
              <LoadError setting="secret alert confidence threshold" />
            ) : (
              <SecretConfidenceThresholdControl
                value={confidence}
                onChange={onConfidenceChange}
                disabled={isLoadingConfidence}
                saving={isSavingConfidence}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
